import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Factory, AlertCircle, CheckCircle2 } from "lucide-react";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1624027492684-327af1fb7559?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxmYWN0b3J5JTIwcHJvY2Vzc2luZyUyMHBsYW50fGVufDB8fHx8MTc4MjU0NDM5M3ww&ixlib=rb-4.1.0&q=85";

const QUICK = [
  { email: "admin@neipl.com", pw: "admin123", label: "Super Admin" },
  { email: "manager@neipl.com", pw: "Demo@123", label: "Site Manager" },
  { email: "maintenance@neipl.com", pw: "Demo@123", label: "Maintenance" },
  { email: "customer@pjl.com", pw: "Demo@123", label: "Customer (PJL)" },
];

export default function Login() {
  const { user, login } = useAuth();
  const branding = useBranding();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.name}`);
      nav("/");
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Hero */}
      <div
        className="hidden lg:block relative"
        style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-slate-900/75" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.name} data-testid="login-logo"
                className="h-10 w-10 rounded-md object-contain bg-white/10" />
            ) : (
              <div className="h-10 w-10 rounded-md bg-sky-500/20 border border-sky-400/40 flex items-center justify-center">
                <Factory className="h-5 w-5 text-sky-300" />
              </div>
            )}
            <div>
              <div className="font-bold tracking-tight text-lg" style={{ fontFamily: "Chivo" }} data-testid="login-company-name">{branding.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-300">{branding.tagline || "O&M Digital Management"}</div>
            </div>
          </div>
          <div>
            <div className="label-cap text-sky-300/70 mb-3">Enterprise · CMMS · Customer Portal</div>
            <h2 className="text-4xl xl:text-5xl font-bold leading-tight" style={{ fontFamily: "Chivo" }}>
              Operate every plant<br />with industrial-grade clarity.
            </h2>
            <p className="mt-5 text-slate-300 max-w-md text-sm leading-relaxed">
              Unified Production, Maintenance, Inventory, Safety and MIS workflows for RDF/AFR, MSW, MRF and CBG facilities — in one secure, role-based platform.
            </p>
            <div className="mt-8 space-y-3.5 max-w-md" data-testid="login-feature-list">
              {[
                "Real-time production, energy & equipment KPIs",
                "Job Orders, Permit-to-Work & lubrication workflows",
                "Project-isolated customer reporting portal",
              ].map((t) => (
                <div key={t} className="flex items-center gap-3 text-sm text-slate-200">
                  <CheckCircle2 className="h-4 w-4 text-sky-300 shrink-0" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-400" data-testid="login-footer">© {new Date().getFullYear()} {branding.name} · All Rights Reserved.</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="label-cap text-slate-500 mb-2">Sign in</div>
          <h1 className="text-3xl font-bold tracking-tight mb-1" style={{ fontFamily: "Chivo" }}>Welcome back</h1>
          <p className="text-slate-500 text-sm mb-8">Enter your credentials to access the system.</p>

          {err && (
            <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-md" data-testid="login-error">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <div>{err}</div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="label-cap">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" required className="mt-1.5"
              />
            </div>
            <div>
              <Label className="label-cap">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required className="mt-1.5"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-sky-900 hover:bg-sky-950 text-white"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="label-cap text-slate-500 mb-3">Quick demo access</div>
            <div className="grid grid-cols-2 gap-2">
              {QUICK.map((q) => (
                <button
                  key={q.email}
                  data-testid={`quick-login-${q.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => { setEmail(q.email); setPassword(q.pw); }}
                  className="text-left text-xs px-3 py-2 border border-slate-200 hover:border-sky-500 hover:bg-sky-50 rounded-md transition"
                >
                  <div className="font-medium text-slate-700">{q.label}</div>
                  <div className="text-slate-400 truncate">{q.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
